import React from 'react';
import classNames from 'classnames';
import {
	PortActionTypes,
	Currencies,
	LaytimeIntervals,
	EstimatorModeTypes,
	LaytimeCalculationMethods,
	shortLaytimeCalculationMethods,
	EstimatePortActionTypes,
	EstimatePortActionTypeLabels,
	ShortEstimatePortActionTypes,
	CargoUnitLabels,
	TimeZoneFormat,
	LinerCostUnits,
	SeaPassageToggles,
} from '@shared/utils/constants';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { Values } from '@shared/utils/objectEnums';
import {
	formatAndRoundNumber,
	formatNumber,
} from '@shared/utils/formatNumber';
import type { Port } from '@api/utils/ports';
import { type ConsumptionSetProps } from '@api/models/consumption-set';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { PerformanceEntryProps } from '@api/models/performance-entry';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import { FuelTypeValue } from '@api/utils/getVesselFuelGrades';
import { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import styles from '../styles/shared.module.css';
import {
	LocalPortRotation,
	PartialPortRotationEntry,
} from '../context/hooks/usePortRotationHandlers';
import PortCostPopover from '../PortCostPopover';
import PortDACellWithHistory from './PortDACellWithHistory';
import {
	formatDays,
	getArrivalColumn,
	getConfigColumn,
	getDeleteColumn,
	getDepartureColumn,
	getDistanceColumn,
	getIdleDaysColumn,
	getIdleTurnUnitColumn,
	getNegativeRobEntryIds,
	getPortColumn,
	getRouteOptionColumns,
	getSeaDaysColumn,
	getSeaMarginColumn,
	getSpeedColumn,
	getTurnDaysColumn,
	getTypeColumn,
	getWorkingDaysColumn,
	isSpecialType,
	renderPortNameWithRobWarning,
} from './portRotationSharedColumns';

type Props = {
	showQtyError: boolean;
	handlePortRotationEntryDeletion: (entry: LocalPortRotation) => void;
	onPortRotationChange: (entries: LocalPortRotation[], immediate?: boolean) => void;
	onPortRotationRowChange: (
		id: number,
		attributes: any
	) => void;
	onConfigUpdate: (id: number, routeSettings: SeaPassageSettings) => void;
	speedPopoverOpen: number | null;
	setSpeedPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	timeZoneFormat: TimeZoneFormat;
	currency: Values<typeof Currencies>;
	ports: Port[];
	consumptionSets: Array<ConsumptionSetProps & { PerformanceEntries: Array<PerformanceEntryProps> }>;
	screens: Partial<Record<string, boolean>>;
	mode: EstimatorModeTypes;
	cargos: CargoDetails[];
	defaultConsumptionSetId: number | undefined;
	disabled?: boolean;
	tcEstimate?: boolean;
	portRotation: LocalPortRotation[];
	waitForLaycan?: boolean;
	handlePortRotationConfigSave: (id: number, routeSettings: SeaPassageSettings) => void;
	handleSeaPassageTogglesSave: (toggles: Partial<SeaPassageToggles>) => void;
	requiredArrivalPopoverOpen: number | null;
	setRequiredArrivalPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	refreshVessel: () => void;
	hasCargoRelet: boolean;
	vesselFuelGrades: Array<FuelTypeValue>;
};

type PortRotationColumn = EditableColumns<LocalPortRotation>[number];

type ColumnBuilderProps = Pick<
	Props,
	'mode' | 'tcEstimate' | 'disabled' | 'hasCargoRelet' | 'currency' | 'cargos' | 'onPortRotationRowChange'
>;

const getDaysColumns = ({
	mode,
	tcEstimate,
	disabled,
	workingDaysDisabled = disabled,
	waitForLaycan,
	hasCargoRelet,
}: Pick<Props, 'mode' | 'tcEstimate' | 'disabled' | 'waitForLaycan' | 'hasCargoRelet'> & {
	workingDaysDisabled?: boolean;
}): PortRotationColumn[] => [
	{
		title: 'Days',
		children: [
			getSeaDaysColumn({
				title: 'Sea',
				renderDays: formatDays,
			}),
			getWorkingDaysColumn({
				disabled: workingDaysDisabled,
				hidden: mode === EstimatorModeTypes.MAP || tcEstimate,
				testId: 'working-days',
				inputProps: {
					className: styles.slimInput,
				},
				renderDays: formatDays,
			}),
		],
	},
	{
		title: tcEstimate ? 'Idle' : 'Idle / Turn',
		hidden: mode === EstimatorModeTypes.MAP,
		onHeaderCell: () => ({
			className: classNames(styles.idleTurnGroupHeader, styles.idleTurnGroupStart, styles.idleTurnGroupEnd),
		}),
		children: [
			getIdleDaysColumn({
				disabled,
				hidden: mode === EstimatorModeTypes.MAP,
				waitForLaycan,
				withIdleTurnUnit: true,
				inputProps: {
					className: styles.slimInput,
					fullWidth: false,
					disabled: hasCargoRelet,
				},
				renderDays: formatDays,
			}),
			getTurnDaysColumn({
				disabled,
				hidden: mode === EstimatorModeTypes.MAP || tcEstimate,
				withIdleTurnUnit: true,
				inputProps: {
					className: styles.slimInput,
					fullWidth: false,
				},
				renderDays: formatDays,
			}),
			getIdleTurnUnitColumn({ disabled }),
		],
	},
];

const getCargoColumnGroup = ({
	mode,
	disabled,
	showQtyError,
}: Pick<Props, 'mode' | 'disabled' | 'showQtyError'>): PortRotationColumn => ({
	title: 'Cargo',
	hidden: mode === EstimatorModeTypes.MAP,
	onHeaderCell: () => ({
		style: {
			backgroundColor: '#ededed',
		},
	}),
	children: [
		{
			onHeaderCell: () => ({
				style: {
					backgroundColor: '#ededed',
				},
			}),
			title: 'Time allowed',
			dataIndex: 'timeAllowedOverride',
			hidden: mode === EstimatorModeTypes.MAP,
			type: 'number',
			inputProps: {
				'data-testid': 'time-allowed',
				className: styles.slimInput,
				fullWidth: false,
			},
			width: 45,
			align: 'right',
			editable: (row) => (row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) && !disabled,
			className: (row) => (
				row.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ?
					styles.disabled : undefined
			),
			renderValue: (p) => (p.value != null ? formatAndRoundNumber(p.value, 2) : null),
		},
		{
			onHeaderCell: () => ({
				style: {
					backgroundColor: '#ededed',
				},
			}),
			title: 'Qty',
			dataIndex: 'qty',
			hidden: mode === EstimatorModeTypes.MAP,
			renderValue: (p) => (p.value != null ? formatAndRoundNumber(p.value, 2) : null),
			type: 'number',
			editable: (row) =>
				((row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) && !disabled) &&
				row.timeAllowedOverride == null,
			className: (row) => {
				if (row.generatedBy !== PortRotationEntryGeneratorTypes.CARGO) {
					return styles.disabled;
				}

				if (row.timeAllowedOverride != null) {
					return styles.calculatedNumber;
				}

				if (showQtyError) {
					return styles.highlightRequired;
				}

				return undefined;
			},
			width: 100,
			inputProps: {
				allowNegative: false,
				className: styles.slimInput,
				fullWidth: false,
			},
		},
		{
			onHeaderCell: () => ({
				style: {
					backgroundColor: '#ededed',
				},
			}),
			title: 'L/D Rate',
			dataIndex: 'loadDischargeRate',
			hidden: mode === EstimatorModeTypes.MAP,
			renderValue: (p) => (p.value != null ? formatAndRoundNumber(p.value, 2) : null),
			type: 'number',
			editable: (row) =>
				((row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) && !disabled) &&
				row.timeAllowedOverride == null,
			className: (row) => {
				if (row.generatedBy !== PortRotationEntryGeneratorTypes.CARGO) {
					return styles.disabled;
				}

				if (row.timeAllowedOverride != null) {
					return styles.calculatedNumber;
				}

				if (row.loadDischargeRate == null) {
					return styles.highlightRequired;
				}

				return undefined;
			},
			width: 60,
			inputProps: {
				'data-testid': 'ld-rate',
				allowNegative: false,
				className: styles.slimInput,
				fullWidth: false,
			},
		},
		{
			onHeaderCell: () => ({
				style: {
					backgroundColor: '#ededed',
				},
			}),
			title: 'Unit',
			dataIndex: 'laytimeInterval',
			hidden: mode === EstimatorModeTypes.MAP,
			type: 'select',
			editable: () => !disabled,
			width: 50,
			inputProps: {
				// @ts-ignore
				'data-testid': 'laytimeInterval',
				placeholder: 'Unit',
				className: styles.slimInput,
				popupMatchSelectWidth: false,
				options: [{
					label: 'D',
					value: LaytimeIntervals.DAY,
				},
				{
					label: 'H',
					value: LaytimeIntervals.HOUR,
				}],
			},
			renderValue: (v) => `${v.value === LaytimeIntervals.DAY ? 'D' : 'H'}`,
		},
		{
			onHeaderCell: () => ({
				style: {
					backgroundColor: '#f0f0f0',
				},
			}),
			title: 'Laytime',
			dataIndex: 'calculationMethod',
			hidden: mode === EstimatorModeTypes.MAP,
			type: 'select',
			editable: (row) => row.generatedBy === PortRotationEntryGeneratorTypes.CARGO && !disabled,
			className: (row) => (
				row.type === PortActionTypes.LOADING || row.type === PortActionTypes.DISCHARGING ?
					undefined :
					styles.disabled
			),
			width: 150,
			inputProps: {
				// @ts-ignore
				'data-testid': 'calculationMethod',
				placeholder: 'Select method',
				className: styles.slimInput,
				showSearch: true,
				popupMatchSelectWidth: false,
				options: Object.values(LaytimeCalculationMethods).map((type) => ({
					label: shortLaytimeCalculationMethods[type],
					value: type,
				})),
			},
			renderValue: (p) => shortLaytimeCalculationMethods[p.value as LaytimeCalculationMethods],
		},
	]
});

const getPortDAsColumn = ({
	mode,
	disabled,
	hasCargoRelet,
	currency,
	cargos,
	onPortRotationRowChange,
	firstEntryId,
}: ColumnBuilderProps & { firstEntryId?: number }): PortRotationColumn => ({
	width: 180,
	title: 'Port DAs',
	dataIndex: 'portCost',
	hidden: mode === EstimatorModeTypes.MAP || hasCargoRelet,
	type: 'currency',
	render: (record: PartialPortRotationEntry) => {
		if (record == null || record.id == null) {
			return null;
		}

		const updateValue = (newVal: number | null) => {
			if (record?.id == null) {
				return;
			}

			onPortRotationRowChange(record.id, { portCost: newVal });
		};

		const rawPortCost = record.portCost;
		const displayPortCost = typeof rawPortCost === 'number' && Number.isFinite(rawPortCost) ? rawPortCost : 0;
		const shouldHighlight = !(typeof displayPortCost === 'number' && Number.isFinite(displayPortCost));

		return (
			<PortDACellWithHistory
				record={record as LocalPortRotation}
				disabled={disabled}
				currency={currency}
				cargos={cargos}
				isRequiredHighlightBorder={shouldHighlight}
				displayPortCost={displayPortCost}
				onValueChange={updateValue}
				onPortRotationRowChange={onPortRotationRowChange}
				shortcut={record.id === firstEntryId ? 'first-port' : undefined}
			/>
		);
	},
});

const getLinerCostColumns = ({
	mode,
	tcEstimate,
	disabled,
	hasCargoRelet,
	currency,
	cargos,
	onPortRotationRowChange,
}: ColumnBuilderProps): PortRotationColumn[] => [
	{
		width: 150,
		title: 'Liner Cost',
		dataIndex: 'linerCostPricePerUnit',
		hidden: mode === EstimatorModeTypes.MAP || tcEstimate || hasCargoRelet,
		type: 'currency',
		render: (record: PartialPortRotationEntry) => {
			if (record == null || record.id == null) {
				return null;
			}

			if (
				record.type !== PortActionTypes.LOADING &&
				record.type !== PortActionTypes.DISCHARGING
			) {
				return null;
			}

			const updateValue = (newVal: number | null) => {
				if (record?.id == null) {
					return;
				}

				onPortRotationRowChange(record.id, { ...record, linerCostPricePerUnit: newVal });
			};

			const relevantCargo = cargos.find((c) => c.id === record.cargoId);

			if (relevantCargo == null) {
				return null;
			}

			return (
				<NumericInput
					disabled={disabled}
					key={record.id}
					value={record.linerCostPricePerUnit ?? 0}
					onChange={updateValue}
					className={styles.currencyPopoverBtn}
					data-testid={'liner-cost'}
					addonBefore={(
						<PortCostPopover
							type="linerCost"
							portRotationId={record.id}
							currency={currency}
							cargo={relevantCargo}
							disabled={disabled}
						/>
					)}
				/>
			);
		},
	},
	{
		width: 110,
		title: 'Liner Cost Unit',
		dataIndex: 'linerCostUnit',
		hidden: mode === EstimatorModeTypes.MAP || tcEstimate || hasCargoRelet,
		type: 'select',
		render: (record: PartialPortRotationEntry) => {
			if (record == null || record.id == null) {
				return null;
			}

			if (
				record.type !== PortActionTypes.LOADING &&
				record.type !== PortActionTypes.DISCHARGING
			) {
				return null;
			}

			const updateValue = (newVal: string | null) => {
				if (record?.id == null) {
					return;
				}

				onPortRotationRowChange(record.id, { linerCostUnit: newVal });
			};

			const relevantCargo = cargos.find((c) => c.id === record.cargoId);

			if (relevantCargo == null) {
				return null;
			}

			const unit = record.linerCostUnit ?? relevantCargo.unit;

			const options = [
				{
					key: 1,
					label: CargoUnitLabels[relevantCargo.unit],
					value: relevantCargo.unit,
				},
				...(relevantCargo.secondaryUnit != null &&
					relevantCargo.secondaryUnit !== relevantCargo.unit ? [{
						key: 2,
						label: CargoUnitLabels[relevantCargo.secondaryUnit],
						value: relevantCargo.secondaryUnit,
					}] : []),
				{
					key: 3,
					label: LinerCostUnits.LS,
					value: LinerCostUnits.LS,
				},
			];

			return (
				<Select
					disabled={disabled}
					key={`${record.id}-linerCostUnit`}
					value={unit}
					options={options}
					onSelect={updateValue}
				/>
			);
		},
	},
];

const getStablePortRotationIndex = (
	row: PartialPortRotationEntry,
	portRotation: PartialPortRotationEntry[],
) => {
	const index = portRotation.findIndex((entry) => entry.id === row.id);

	if (index !== -1) {
		return index;
	}

	if (row.generatedBy != null) {
		const generatedIndex = portRotation.findIndex(
			(entry) => entry.generatedBy === row.generatedBy,
		);

		if (generatedIndex !== -1) {
			return generatedIndex;
		}
	}

	return 0;
};

const getPortSelectTestId = (
	row: PartialPortRotationEntry,
	rowIndex: number,
) => {
	const stableIndex = rowIndex < 0 ? 0 : rowIndex;

	if (row.generatedBy != null) {
		const slug = row.generatedBy.toLowerCase().replace(/_/g, '-');

		if (row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) {
			return `port-select-cargo-${stableIndex}`;
		}

		return `port-select-${slug}-${stableIndex}`;
	}

	const typeSlug = (
		ShortEstimatePortActionTypes[row.type as keyof typeof ShortEstimatePortActionTypes] ??
		row.type
	)?.toLowerCase();

	return `port-select-${typeSlug}-${stableIndex}`;
};

export const getPortRotationColumns = ({
	showQtyError,
	handlePortRotationEntryDeletion,
	onConfigUpdate,
	onPortRotationRowChange,
	speedPopoverOpen,
	setSpeedPopoverOpen,
	timeZoneFormat,
	currency,
	ports,
	consumptionSets,
	mode,
	cargos,
	defaultConsumptionSetId,
	disabled,
	tcEstimate = false,
	portRotation,
	onPortRotationChange,
	waitForLaycan,
	handlePortRotationConfigSave,
	handleSeaPassageTogglesSave,
	requiredArrivalPopoverOpen,
	setRequiredArrivalPopoverOpen,
	refreshVessel,
	hasCargoRelet,
	vesselFuelGrades,
}: Props): EditableColumns<LocalPortRotation> => {
	const negativeRobEntryIds = getNegativeRobEntryIds(portRotation, vesselFuelGrades);
	const columnBuilderProps = {
		mode,
		tcEstimate,
		disabled,
		hasCargoRelet,
		currency,
		cargos,
		onPortRotationRowChange,
	};

	return [
		getDeleteColumn({
			onDelete: handlePortRotationEntryDeletion,
			disabled,
		}),
		getConfigColumn({
			onConfigSave: handlePortRotationConfigSave,
			onSeaPassageTogglesSave: handleSeaPassageTogglesSave,
			portRotation,
			disabled,
		}),
		getTypeColumn({
			disabled,
			actionTypes: EstimatePortActionTypes,
			actionTypeLabels: EstimatePortActionTypeLabels,
			shortActionTypeLabels: ShortEstimatePortActionTypes,
		}),
		getPortColumn({
			ports,
			disabled,
			isGeneratedPortEditable: (generatedBy) => isSpecialType(generatedBy, false),
			inputPropsWithRow: (row: PartialPortRotationEntry) => ({
				'data-testid': getPortSelectTestId(
					row,
					getStablePortRotationIndex(row, portRotation),
				),
				...(row.id === portRotation[0]?.id ? { 'data-shortcut': 'first-port' } : {}),
			}),
			renderValue: (p: any) => renderPortNameWithRobWarning(
				p.inputProps?.options?.find((o: any) => o.value === p.value)?.label ?? null,
				negativeRobEntryIds.has(p.row?.id),
			),
		}),
		getArrivalColumn({
			timeZoneFormat,
			cargos,
			portRotation,
			disabled,
			consumptionSets,
			requiredArrivalPopoverOpen,
			setRequiredArrivalPopoverOpen,
			onPortRotationRowChange,
			refreshVessel,
		}),
		getDepartureColumn({
			timeZoneFormat,
			disabled,
			localTimeWidth: 400,
		}),
		getDistanceColumn({
			renderDistance: (value) => formatNumber(value),
		}),
		getSeaMarginColumn({ disabled }),
		getSpeedColumn({
			portRotation,
			consumptionSets,
			defaultConsumptionSetId,
			disabled,
			hasCargoRelet,
			speedPopoverOpen,
			setSpeedPopoverOpen,
			onPortRotationChange,
			onPortRotationRowChange,
		}),
		{
			title: 'Draft',
			dataIndex: 'draft',
			type: 'number',
			inputProps: {
				className: styles.slimInput,
				addonAfter: 'm',
			},
			width: 55,
			editable: !disabled,
			className: styles.calculatedNumber,
			renderValue: (p) => (p.value != null ? formatAndRoundNumber(p.value, 2) : ''),
		},
		...getDaysColumns({
			mode,
			tcEstimate,
			disabled,
			waitForLaycan,
			hasCargoRelet,
		}),
		getCargoColumnGroup({
			mode,
			disabled,
			showQtyError,
		}),
		getPortDAsColumn(columnBuilderProps),
		...getLinerCostColumns(columnBuilderProps),
		...getRouteOptionColumns({
			mode,
			disabled,
			onConfigUpdate,
		}),
	];
};

export const getPortsModalColumns = ({
	showQtyError,
	onPortRotationRowChange,
	onPortRotationChange,
	handlePortRotationConfigSave,
	handleSeaPassageTogglesSave,
	currency,
	ports,
	mode,
	cargos,
	disabled,
	waitForLaycan,
	hasCargoRelet,
	portRotation,
	consumptionSets,
	defaultConsumptionSetId,
	speedPopoverOpen,
	setSpeedPopoverOpen,
}: Pick<
	Props,
	'showQtyError' | 'onPortRotationRowChange' | 'onPortRotationChange' | 'handlePortRotationConfigSave' |
	'handleSeaPassageTogglesSave' |
	'currency' | 'ports' | 'mode' | 'cargos' | 'disabled' | 'waitForLaycan' | 'hasCargoRelet' |
	'portRotation' | 'consumptionSets' | 'defaultConsumptionSetId' | 'speedPopoverOpen' | 'setSpeedPopoverOpen'
>): EditableColumns<LocalPortRotation> => {
	const columnBuilderProps = {
		mode,
		disabled,
		hasCargoRelet,
		currency,
		cargos,
		onPortRotationRowChange,
	};

	return [
		getConfigColumn({
			onConfigSave: handlePortRotationConfigSave,
			onSeaPassageTogglesSave: handleSeaPassageTogglesSave,
			portRotation,
			disabled,
		}),
		getTypeColumn({
			disabled: true,
			actionTypes: EstimatePortActionTypes,
			actionTypeLabels: EstimatePortActionTypeLabels,
			shortActionTypeLabels: ShortEstimatePortActionTypes,
		}),
		getPortColumn({
			ports,
			disabled: true,
			isGeneratedPortEditable: () => false,
		}),
		getPortDAsColumn({ ...columnBuilderProps, firstEntryId: portRotation[0]?.id }),
		getSeaMarginColumn({ disabled }),
		getSpeedColumn({
			portRotation,
			consumptionSets,
			defaultConsumptionSetId,
			disabled,
			hasCargoRelet,
			speedPopoverOpen,
			setSpeedPopoverOpen,
			onPortRotationChange,
			onPortRotationRowChange,
		}),
		...getDaysColumns({
			mode,
			disabled,
			workingDaysDisabled: true,
			waitForLaycan,
			hasCargoRelet,
		}),
		getCargoColumnGroup({
			mode,
			disabled,
			showQtyError,
		}),
		...getLinerCostColumns(columnBuilderProps),
	];
};
